"use client";
import { SearchIcon } from "@heroicons/react/outline";
import { useMemo } from "react";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { ITeam } from "@gettactic/api";
import { UnifiedSelect, SelectItem } from "@cmp-src/form/UnifiedSelect";
import { cn } from "@gettactic/helpers";
import { ChevronDownIcon } from "lucide-react";

type Props = {
  onChange: (ids: string[]) => void;
  texts?: any;
  teams?: string[];
  includeFavs: boolean;
  disabled?: boolean;
  /** Visual variant - 'default' has border, 'ghost' has no border */
  variant?: "default" | "ghost";
  /** Show the selected label in trigger */
  showSelectedLabel?: boolean;
  /** Custom trigger class */
  triggerClassName?: string;
  /** Show split view with selected items panel */
  splitView?: boolean;
  /** Width of the dropdown content */
  contentWidth?: string;
};

export const SelectTeams = ({
  onChange,
  texts = {},
  teams = [],
  disabled = false,
  includeFavs = false,
  variant = "default",
  showSelectedLabel = true,
  triggerClassName,
  splitView = false,
  contentWidth
}: Props) => {
  const placeholderText = includeFavs
    ? "Select your lists or teams"
    : "Select teams";

  const { teamList } = useTeams({ type: "directory", hidden: false });
  const { teamList: groups } = useTeams({ type: "group", hidden: false });

  const items: SelectItem<ITeam>[] = useMemo(() => {
    const elements = (teamList ? teamList.elements : []).concat(
      groups && includeFavs ? groups.elements : []
    );
    return elements
      .map((x: ITeam) => ({ id: x.id, name: x.name, data: x }))
      .sort((a: SelectItem<ITeam>, b: SelectItem<ITeam>) =>
        a.name.localeCompare(b.name)
      );
  }, [teamList, groups, includeFavs]);

  const selectedItems = useMemo(() => {
    return items.filter((item) => teams.includes(item.id));
  }, [items, teams]);

  if (!teamList || !groups) {
    return null;
  }

  // Default trigger content for ghost variant
  const getDefaultTriggerContent = () => {
    if (selectedItems.length === 0) {
      return (
        <>
          {showSelectedLabel && (
            <span className="truncate text-muted-foreground font-inter">
              {placeholderText}
            </span>
          )}
        </>
      );
    }
    if (selectedItems.length === 1) {
      return (
        <>
          {showSelectedLabel && (
            <span className="truncate font-inter">{selectedItems[0].name}</span>
          )}
        </>
      );
    }
    return (
      <>
        {showSelectedLabel && (
          <span className="truncate font-inter">
            {selectedItems.length} selected
          </span>
        )}
      </>
    );
  };

  const triggerStyles =
    variant === "default"
      ? "flex h-10 w-full min-w-0 items-center justify-between gap-1 cursor-pointer text-sm rounded-lg border border-input bg-background px-3 py-2 hover:bg-accent/50"
      : "flex h-9 min-w-0 max-w-[200px] items-center gap-1 cursor-pointer text-sm";

  const triggerContent =
    variant === "ghost" ? (
      <span className={cn(triggerStyles, triggerClassName)}>
        <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
          {getDefaultTriggerContent()}
        </span>
        <ChevronDownIcon className={"size-4"} />
      </span>
    ) : undefined;

  return (
    <UnifiedSelect
      items={items}
      values={selectedItems}
      onSelectMultiple={(selected) => {
        onChange(selected.map((item) => item.id));
      }}
      disabled={disabled}
      multiselect
      withApply
      searchable
      searchPlaceholder="Search teams..."
      placeholder={placeholderText}
      triggerClassName={
        variant === "default" ? cn(triggerStyles, triggerClassName) : undefined
      }
      trigger={triggerContent}
      splitView={splitView}
      contentWidth={contentWidth}
      renderValue={
        variant === "default"
          ? (selected) => {
              if (
                !selected ||
                (Array.isArray(selected) && selected.length === 0)
              ) {
                return (
                  <span className="flex items-center text-neutral-600">
                    <SearchIcon className="h-5 w-5" />
                    <span className="pl-2">{placeholderText}</span>
                  </span>
                );
              }
              if (Array.isArray(selected)) {
                return selected.length === 1
                  ? selected[0].name
                  : `${selected.length} teams selected`;
              }
              return selected.name;
            }
          : undefined
      }
    />
  );
};
